import { Fragment, useId, useState, type CSSProperties } from 'react';

import { ChevronDownIcon } from '@commenda/pathfinder/icons/ChevronDownIcon';
import { ChevronRightIcon } from '@commenda/pathfinder/icons/ChevronRightIcon';

import type { StructureLegendSection } from '../adapters';
import { StructureCanvasHelper } from '../canvas';
import type { LayoutLine } from '../layout';

interface StructureLegendProps {
  lines: ReadonlyArray<LayoutLine>;
  // Rows the chart kind adds, such as the tax chart's shapes.
  section: Readonly<StructureLegendSection> | null;
}

const legendStyle: CSSProperties = {
  position: 'absolute',
  bottom: 16,
  left: 16,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '8px 8px 10px 12px',
  borderRadius: 'var(--pf-radius-md)',
  border: '0.5px solid var(--pf-color-soft-pearl-300)',
  background: 'var(--pf-color-background)',
  boxShadow: 'var(--pf-shadow-raised)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  font: '400 12px/16px var(--pf-font-body)',
  color: 'var(--pf-color-soft-pearl-900)',
  textAlign: 'left',
};

const headerLabelStyle: CSSProperties = {
  flex: 1,
};

const chevronStyle: CSSProperties = {
  display: 'flex',
  width: 12,
  height: 12,
};

// The sample column is as wide as the widest sample shown, so labels line up across sections.
const rowsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  alignItems: 'center',
  gap: 8,
};

const labelStyle: CSSProperties = {
  font: '400 12px/18px var(--pf-font-body)',
  color: 'var(--pf-color-soft-pearl-800)',
  whiteSpace: 'nowrap',
};

const sectionTitleStyle: CSSProperties = {
  gridColumn: '1 / -1',
  font: '500 11px/16px var(--pf-font-body)',
  color: 'var(--pf-color-soft-pearl-700)',
};

const sampleStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
};

const sampleLineStyle: CSSProperties = {
  stroke: 'var(--pf-color-soft-pearl-600)',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
};

const LineSample = ({ dash }: { dash: string | undefined }) => (
  <svg aria-hidden="true" width={20} height={14}>
    <line
      x1={1}
      y1={7}
      x2={19}
      y2={7}
      strokeDasharray={dash}
      style={sampleLineStyle}
    />
  </svg>
);

// One box for everything the chart's marks mean; line styles only show once the chart mixes them.
const StructureLegend = ({ lines, section }: StructureLegendProps) => {
  const [isLegendExpanded, setIsLegendExpanded] = useState(true);
  const rowsId = useId();
  const lineSection: StructureLegendSection = {
    title: 'Lines',
    rows: StructureCanvasHelper.getLegendEntries(lines).map((entry) => ({
      label: entry.label,
      sample: (
        <LineSample dash={StructureCanvasHelper.LINE_DASH[entry.kinds[0]]} />
      ),
    })),
  };
  const sections = [section, lineSection].filter(
    (candidate): candidate is StructureLegendSection =>
      candidate !== null && candidate.rows.length > 0
  );
  if (sections.length === 0) return null;
  const showsTitles = sections.length > 1;
  return (
    <div style={legendStyle}>
      <button
        type="button"
        aria-expanded={isLegendExpanded}
        aria-controls={rowsId}
        onClick={() => setIsLegendExpanded((isExpanded) => !isExpanded)}
        style={headerStyle}
      >
        <span style={headerLabelStyle}>Legend</span>
        <span aria-hidden="true" style={chevronStyle}>
          {isLegendExpanded ? (
            <ChevronDownIcon size={12} />
          ) : (
            <ChevronRightIcon size={12} />
          )}
        </span>
      </button>
      {isLegendExpanded && (
        <div id={rowsId} style={rowsStyle}>
          {sections.map((current) => (
            <Fragment key={current.title}>
              {showsTitles && (
                <span style={sectionTitleStyle}>{current.title}</span>
              )}
              {current.rows.map(({ label, sample }) => (
                <Fragment key={label}>
                  <span aria-hidden="true" style={sampleStyle}>
                    {sample}
                  </span>
                  <span style={labelStyle}>{label}</span>
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export { StructureLegend };
